<script lang="ts">
import { defineComponent, PropType } from 'vue'
import SolutionName from '@/components/solutions/SolutionName.vue'
import ExternalHtmlWrapper from '@/components/_core/ExternalHtmlWrapper.vue'
import solutionCategory from '@/models/solutionCategory'
import { defaultProps, defaultFunction } from '@/components/_parials/contentsCard'

export default defineComponent({
  name: 'SolutionContentsCard',

  components: {
    SolutionName,
    ExternalHtmlWrapper,
  },

  props: {
    ...defaultProps,

    item: {
      type: Object as PropType<SolutionContentItem>,
      required: true,
    },

    hasSolutionLink: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    const {
      // computed
      sizeStateClass,
      linkTo,
      highlightHeading,
      highlightDescription,
      wrapperTagName,
      computedHeadingOptions,
      computedImageOptions,
      cssVariables,
    } = defaultFunction(props)
    const { getSolutionCategoryLabel } = solutionCategory()

    return {
      // computed
      sizeStateClass,
      linkTo,
      highlightHeading,
      highlightDescription,
      wrapperTagName,
      computedHeadingOptions,
      computedImageOptions,
      cssVariables,

      // methods
      getSolutionCategoryLabel,
    }
  },
})
</script>

<template>
  <component
    :is="wrapperTagName"
    :class="[
      'l-unit__solutionContentsCard',
      sizeStateClass,
    ]"
    :href="linkTo"
    :data-horizontal-pc="horizontal.pc"
    :data-horizontal-sp="horizontal.sp"
    :style="cssVariables">
    <div class="l-unit__solutionContentsCard--header">
      <div class="o-heading__solutionContentsCard">
        <component
          :is="computedHeadingOptions.tagName"
          class="o-heading__solutionContentsCard--item"
          :style="{
            'max-height': computedHeadingOptions.maxHeight,
            'line-height': computedHeadingOptions.lineHeight,
            'font-weight': computedHeadingOptions.fontWeight,
            '--font-size-pc': computedHeadingOptions.fontSize.pc,
            '--font-size-sp': computedHeadingOptions.fontSize.sp,
          }"
          v-html="highlightHeading" />
      <!-- /.o-heading__solutionContentsCard --></div>
    <!-- /.l-unit__solutionContentsCard header --></div>

    <div
      v-if="hasHero && item.thumbImageUrl"
      class="l-unit__solutionContentsCard--image">
      <img
        class="img"
        :src="item.thumbImageUrl"
        loading="lazy"
        alt="">
    <!-- /.l-unit__solutionContentsCard image --></div>

    <div
      v-if="item.description"
      class="l-unit__solutionContentsCard--description">
      <div class="o-label__description">
        <p
          class="o-label__description--item"
          v-html="highlightDescription" />
      <!-- /.o-label__description --></div>
    </div>

    <ExternalHtmlWrapper
      v-if="item.introduction"
      :class-names="[
        'l-unit__solutionContentsCard--description',
        'is-introduction',
      ]"
      :contents-html="item.introduction" />

    <div
      v-if="item.solution?.categoryIds?.length"
      class="l-unit__solutionContentsCard--footer is-category">
      <div class="o-list__category">
        <ul class="o-list__category--items">
          <li
            v-for="id in item.solution.categoryIds"
            :key="id"
            class="o-list__category--item">
            <div class="o-label__category">
              <p class="o-label__category--item">
                {{ getSolutionCategoryLabel(id) }}
              </p>
            <!-- /.o-label__category --></div>
          </li>
        </ul>
      <!-- /.o-list__category --></div>
    <!-- /.l-unit__solutionContentsCard footer --></div>

    <div
      v-if="item.solution"
      :class="[
        'l-unit__solutionContentsCard--footer',
        'is-name',
        { 'is-link': hasSolutionLink },
      ]">
      <SolutionName
        :solution="item.solution"
        :has-link="hasSolutionLink" />
    <!-- /.l-unit__solutionContentsCard footer --></div>
  <!-- /.l-unit__solutionContentsCard --></component>
</template>

<style lang="sass" scoped>
// layout
.l-unit__solutionContentsCard
  $root: &
  +base-layout($image-position: top, $root: $root)
  text-decoration: none
  margin-top: 1em
  +transition

  &:first-child
    margin-top: 0

  &[href]
    +mouseover
      #{$root}--image
        opacity: 0.5

      .o-heading__solutionContentsCard--item
        text-decoration: underline

    &:has(#{$root}--footer.is-name.is-link:hover)
      .o-heading__solutionContentsCard--item
        text-decoration: none

      #{$root}--image
        opacity: 1

  & &--header
    flex-grow: 1

  & &--image
    position: relative
    margin-bottom: 0.6em
    +transition

    &::before
      content: ""
      width: 0
      display: block
      padding-top: 50%

    &:first-child:last-child
      margin-bottom: 0

    > .img
      height: 100%
      display: block
      object-fit: cover
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)

  & &--footer
    &.is-category
      order: -1
      margin-bottom: 0.4em

      &:last-child
        margin-bottom: 0

    &.is-name
      margin-top: 0.6em

  &.is-full
    height: auto
    min-height: 0
    flex-direction: row
    flex-wrap: wrap

    #{$root}--header,
    #{$root}--image,
    #{$root}--description,
    #{$root}--footer
      width: 100%

    #{$root}--header
      flex-grow: 0

    #{$root}--image
      > .img
        object-fit: contain

    #{$root}--description
      order: 1
      flex-grow: 1
      margin-top: 1.5em

    #{$root}--footer
      &.is-category
        margin-bottom: 0.8em

      &.is-name,
      &.is-date
        width: auto
        line-height: 1

      &.is-date
        margin-left: 1.4em

  +breakpoint($max: false, $min: true)
    &[data-horizontal-pc="true"]
      $image-width: 200
      $image-height: 100
      $margin: 16
      +base-layout($image-position: left, $root: $root, $image-width: $image-width, $image-height: $image-height, $margin-size: $margin)
      --image-width-pc: #{$image-width}
      --image-height-pc: #{$image-height}
      $image-area-size: calc(px(var(--image-width-pc)) + #{$margin}px)
      min-height: px(var(--image-height-pc))
      padding-left: $image-area-size

      #{$root}--header
        flex-grow: 0
        margin-bottom: 0.6em

      #{$root}--image
        width: px(var(--image-width-pc))
        height: px(var(--image-height-pc))
        margin-bottom: 0

  +breakpoint
    &.is-normal
      #{$root}--footer
        &.is-category
          display: none

    &.is-full
      +base-layout($image-position: top, $root: $root)

      #{$root}--footer
        &.is-category
          display: block

    &[data-horizontal-sp="true"]
      $image-width: 140
      $image-height: 72
      $margin: 12
      +base-layout($image-position: left, $root: $root, $image-width: $image-width, $image-height: $image-height, $margin-size: $margin)
      --image-width-sp: #{$image-width}
      --image-height-sp: #{$image-height}
      $image-area-size: calc(px(var(--image-width-sp)) + #{$margin}px)
      min-height: px(var(--image-height-sp))
      padding-left: $image-area-size

      #{$root}--header
        margin-bottom: 0.6em

      #{$root}--image
        width: px(var(--image-width-sp))
        height: px(var(--image-height-sp))
        margin-bottom: 0

      #{$root}--footer
        &.is-category
          display: none

// object
.o-heading__solutionContentsCard
  $root: &
  --font-size-pc: $font-large
  --font-size-sp: $font-normal

  & &--item
    overflow: hidden // style 属性で template 側から max-height を渡して props で設定された最大行数以降は非表示にする
    line-height: 1.8
    color: $font-black
    font-size: var(--font-size-pc)

  +breakpoint
    #{$root}--item
      line-height: 1.4
      font-size: var(--font-size-sp)

.o-list__category
  $root: &
  $gutter-size: 0.4em
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--items
    display: flex
    flex-wrap: wrap
    margin:
      top: -$gutter-size
      left: -$gutter-size

  & &--item
    margin:
      top: $gutter-size
      left: $gutter-size

.o-label__category
  & &--item
    display: inline-block
    line-height: 1.7
    color: $font-orange
    font-size: $font-x-small
    padding: 0 0.5em
    background-color: rgba($bg-white, 1)
    border: 1px solid $border-orange
    border-radius: $border-radius

.o-label__description
  & &--item
    color: $font-light-gray
    font-size: $font-small
</style>
